import React, { useState } from 'react';
import { Upload, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { UniversalColorPicker } from '../shared/UniversalColorPicker';
import { useUploadAssetMutation } from '../../features/assets/assetsApi';
import { toast } from 'react-hot-toast';
import { normalizeId } from '../../utils/assetUtils';

export interface BackgroundSettings {
  type: 'solid' | 'gradient' | 'image';
  solidColor: string;
  opacity: number;
  gradientStart: string;
  gradientEnd: string;
  gradientAngle: number;
  imageUrl?: string;
  assetId?: string;
  imageFit: 'cover' | 'contain' | 'fill';
  blur: number;
  overlayColor: string;
  overlayOpacity: number;
}

interface BackgroundPropertiesPanelProps {
  selectedPanelId: string | null;
  settings: BackgroundSettings;
  onSettingsChange: (settings: Partial<BackgroundSettings>, commit?: boolean) => void;
  onClose: () => void;
}

const PropertyInput: React.FC<{
  label?: string;
  value: number;
  onChange: (val: number) => void;
  width?: string;
  unit?: string;
  step?: number;
  min?: number;
}> = ({ label, value, onChange, width = "w-9", unit, step = 1, min }) => (
  <div className="flex items-center h-7 px-2 rounded-lg bg-gray-50 border border-transparent hover:border-gray-200 focus-within:border-[#30A84B] focus-within:bg-white focus-within:shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all group relative">
    {label && (
      <span className="text-[9px] font-black text-gray-400 mr-1.5 select-none uppercase tracking-widest group-focus-within:text-[#30A84B]">
        {label}
      </span>
    )}
    <input
      type="number"
      value={typeof value === 'number' ? (step < 1 ? value : Math.round(value)) : 0}
      onChange={(e) => {
        const val = parseFloat(e.target.value);
        if (!isNaN(val)) onChange(val);
      }}
      className={`${width} bg-transparent text-[11px] font-bold text-gray-700 outline-none text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none pl-0.5`}
      step={step}
      min={min}
    />
    {unit && <span className="text-[10px] text-gray-400 font-bold ml-0.5">{unit}</span>}
    <div className="flex flex-col ml-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(value + step); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronUp className="w-2.5 h-2.5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(min !== undefined ? Math.max(min, value - step) : value - step); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
    </div>
  </div>
);

export const BackgroundPropertiesPanel: React.FC<BackgroundPropertiesPanelProps> = ({
  selectedPanelId,
  settings,
  onSettingsChange,
}) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [uploadAsset, { isLoading: isUploading }] = useUploadAssetMutation();

  const updateSetting = (key: keyof BackgroundSettings, value: any, commit = false) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    onSettingsChange({ [key]: value }, commit);
  };

  const updateSettings = (updates: Partial<BackgroundSettings>, commit = false) => {
    const newSettings = { ...localSettings, ...updates };
    setLocalSettings(newSettings);
    onSettingsChange(updates, commit);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);

        const response = await uploadAsset(formData).unwrap() as any;
        const imageUrl = response?.data?.url || response?.url;
        const assetId = normalizeId(response?.data?._id || response?._id);

        if (imageUrl) {
          updateSettings({ imageUrl, assetId, type: 'image' }, true);
          toast.success('Background image uploaded successfully');
        }
      } catch (error) {
        console.error('Failed to upload background image:', error);
        toast.error('Failed to upload background image');
      } finally {
        // Reset input
        e.target.value = '';
      }
    }
  };

  if (!selectedPanelId) return null;

  return (
    <div className="flex items-center gap-4 py-1 h-full w-max">
      {/* Background Type */}
      <div className="flex items-center gap-1 px-3 border-r border-gray-100 h-8">
        {(['solid', 'gradient', 'image'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => updateSetting('type', type)}
            className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-colors ${localSettings.type === type
              ? 'bg-green-100 text-green-700'
              : 'text-gray-500 hover:bg-gray-100'
              }`}
          >
            {type}
          </button>
        ))}
      </div>
      {/* proce */}
      {/* Standardized Color Picker */}
      <div className="px-3 border-r border-gray-100 h-8 flex items-center">
        <UniversalColorPicker
          label="Background Fill"
          value={localSettings.type === 'gradient' ? {
            type: 'gradient',
            gradientStart: localSettings.gradientStart,
            gradientEnd: localSettings.gradientEnd,
            gradientAngle: localSettings.gradientAngle,
            color: localSettings.solidColor
          } : {
            type: 'solid',
            color: localSettings.solidColor
          }}
          onChange={(newFill) => {
            if (newFill.type === 'solid') {
              updateSettings({
                type: 'solid',
                solidColor: newFill.color || '#ffffff'
              }, true);
            } else {
              updateSettings({
                type: 'gradient',
                gradientStart: newFill.gradientStart || '#ffffff',
                gradientEnd: newFill.gradientEnd || '#000000',
                gradientAngle: newFill.gradientAngle || 0
              }, true);
            }
          }}
        />
      </div>

      {localSettings.type === 'image' && (
        <div className="flex items-center gap-3 px-3 border-r border-gray-100 h-8">
          <label className="flex items-center gap-2 cursor-pointer group">
            <div className="w-6 h-6 rounded bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center group-hover:border-[#30A84B] group-hover:bg-green-50 transition-all overflow-hidden relative">
              {isUploading ? (
                <Loader2 className="w-3 h-3 text-[#30A84B] animate-spin" />
              ) : localSettings.imageUrl ? (
                <img src={localSettings.imageUrl} className="w-full h-full object-cover" crossOrigin="anonymous" />
              ) : (
                <Upload className="w-3 h-3 text-gray-400 group-hover:text-[#30A84B]" />
              )}
            </div>
            <span className="text-[10px] font-bold text-gray-400 group-hover:text-[#30A84B] uppercase tracking-widest whitespace-nowrap">
              {localSettings.imageUrl ? 'Change' : 'Upload'}
            </span>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={isUploading}
            />
          </label>
        </div>
      )}

      {/* Opacity */}
      <div className="flex items-center gap-2 px-3 border-r border-gray-100 h-8">
        <span className="text-[10px] text-gray-400 font-bold uppercase">Opacity</span>
        <input
          type="range"
          min="0"
          max="100"
          value={localSettings.opacity ?? 100}
          onChange={(e) => updateSetting('opacity', parseInt(e.target.value), false)}
          onMouseUp={(e) => updateSetting('opacity', parseInt((e.target as HTMLInputElement).value), true)}
          onBlur={(e) => updateSetting('opacity', parseInt((e.target as HTMLInputElement).value), true)}
          className="w-20 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
        />
        <span className="text-[10px] text-gray-600 font-bold w-6">{localSettings.opacity ?? 100}%</span>
      </div>

      {/* Effects */}
      <div className="flex items-center gap-3 px-3">
        <div className="flex items-center group">
          <span className="text-[10px] text-gray-400 font-bold uppercase mr-1.5 group-focus-within:text-[#30A84B] transition-colors">Blur</span>
          <PropertyInput
            value={localSettings.blur ?? 0}
            onChange={(val) => updateSetting('blur', val)}
            width="w-7"
            min={0}
          />
        </div>
      </div>
    </div>
  );
};

